/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from 'src/components/ts-components/Text'
import { useRegistrationSetupContext } from '../../context'
import { App, Image, Upload, Tooltip } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import RichTextEditor from '../CustomForm/RichTextEditor'
import PreviewIcon from '../CustomForm/PreviewIcon'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'

const Customization = () => {
  const { state, setState, scheduleId, isUpdated } =
    useRegistrationSetupContext()
  const { message } = App.useApp()

  /**
   * Validates the uploaded file to ensure it is an image and under 2MB.
   * Allows any image format.
   */
  const beforeUpload = (file: File): boolean => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error('You can only upload image files!')
      return false
    }
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error('Image must be smaller than 10MB!')
      return false
    }
    return true
  }

  const handleChangeBackground = (info: any) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setState({
        ...state,
        customBackgroundImage: reader.result as string,
        customBackgroundImageChanged: true,
      })
    })
    if (info.file.originFileObj) {
      reader.readAsDataURL(info.file.originFileObj)
    }
  }

  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          align-self: stretch;
          width: 100%;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Customize Header
        </Text>

        {(() => {
          const teamEnabled = state.enableTeamRegistration
          const playerEnabled = state.enablePlayerRegistration
          const bothEnabled = teamEnabled && playerEnabled
          const anyEnabled = teamEnabled || playerEnabled
          const targetFormType =
            bothEnabled || teamEnabled
              ? 'team'
              : playerEnabled
              ? 'player'
              : null

          if (isUpdated && anyEnabled && targetFormType) {
            return (
              <Link
                to={`${ROUTES.REGISTRATION_PREVIEW.replace(
                  ':id',
                  scheduleId
                )}?form_type=${targetFormType}`}
                target='_blank'
                rel='noopener noreferrer'
                css={css`
                  border: 1px solid ${colors.PRIMARY};
                  border-radius: 8px;
                  padding: 4px 8px;
                  justify-content: flex-start;
                  align-items: center;
                  gap: 8px;
                  display: flex;
                  cursor: pointer;
                  text-decoration: none;
                  &:hover {
                    opacity: 0.8;
                  }
                `}
              >
                <PreviewIcon />
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.PRIMARY}
                >
                  Preview
                </Text>
              </Link>
            )
          }

          const tooltipText = !isUpdated
            ? 'Please save the registration form first to preview the form'
            : 'Please enable at least one registration type to preview'

          return (
            <Tooltip
              title={
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  {tooltipText}
                </Text>
              }
              placement='bottom'
              zIndex={10010}
            >
              <div
                css={css`
                  border: 1px solid ${colors.NEUTRAL_30};
                  border-radius: 8px;
                  padding: 4px 8px;
                  justify-content: flex-start;
                  align-items: center;
                  gap: 8px;
                  display: flex;
                  cursor: not-allowed;
                  opacity: 0.5;
                `}
              >
                <PreviewIcon color={colors.NEUTRAL_30} />
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.NEUTRAL_30}
                >
                  Preview
                </Text>
              </div>
            </Tooltip>
          )
        })()}
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          flex-direction: row;
          gap: 16px;
        `}
      >
        {state.customBackgroundImage ? (
          <div
            css={css`
              display: flex;
              padding: 8px;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              border-radius: 8px;
              background-color: ${colors.SECONDARY_100};
              border: 1px solid ${colors.SECONDARY_200};
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <Image
                src={state.customBackgroundImage}
                alt='background'
                width={160}
                height={48}
                css={css`
                  border-radius: 4px;
                  object-fit: cover;
                `}
                preview={false}
              />
              <DeleteOutlined
                css={css`
                  color: ${colors.ERROR_200};
                  font-size: 24px;
                  cursor: pointer;
                `}
                onClick={() => {
                  setState({
                    ...state,
                    customBackgroundImage: '',
                    customBackgroundImageChanged: true,
                  })
                }}
              />
            </div>
          </div>
        ) : (
          <Upload
            beforeUpload={beforeUpload}
            onChange={handleChangeBackground}
            showUploadList={false}
            customRequest={({ onSuccess }) => onSuccess?.('ok' as any)}
          >
            <div
              css={css`
                display: flex;
                padding: 8px;
                align-items: center;
                gap: 8px;
                align-self: stretch;
                border-radius: 8px;
                border: 1px dashed ${colors.SECONDARY_200};
                width: 100%;
                cursor: pointer;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  flex: 1 0 0;
                  align-self: stretch;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    width: 48px;
                    height: 48px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    border-radius: 4px;
                    background: ${colors.PRIMARY};
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      width: 24px;
                      height: 24px;
                      flex-direction: column;
                      justify-content: center;
                      align-items: center;
                      border-radius: 4px;
                      background: ${colors.WHITE};
                    `}
                  >
                    <PlusOutlined
                      css={css`
                        font-size: 24px;
                        color: ${colors.HEADER};
                        background-color: ${colors.PRIMARY};
                      `}
                    />
                  </div>
                </div>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 4px;
                    `}
                  >
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.NEUTRAL_0}
                    >
                      Custom Cover
                    </Text>
                    <Text
                      variant='paragraphXSmall'
                      weight='medium'
                      color={colors.NEUTRAL_100}
                    >
                      (Recommended: 1440x300)
                    </Text>
                  </div>
                  <Text
                    variant='paragraphSmall'
                    weight='medium'
                    color={colors.PRIMARY}
                  >
                    Click to Upload
                  </Text>
                </div>
              </div>
            </div>
          </Upload>
        )}
      </div>
      {/* Custom Cover helper */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          align-self: stretch;
          width: 100%;
          display: none;
        `}
      >
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style={'align-self: flex-end;'}
        >
          The custom cover image appears behind your registration header.
        </Text>
      </div>

      {/* Header Content Section */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          align-self: stretch;
          width: 100%;
        `}
      >
        <RichTextEditor
          label='Custom Header Content'
          value={state.headerContent || ''}
          onChange={(value) => {
            setState({ ...state, headerContent: value })
          }}
          placeholder='Enter additional information that will appear at the top of the registration form...'
        />
      </div>
      {/* Header content helper */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          align-self: stretch;
          width: 100%;
          display: none;
        `}
      >
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style={'align-self: flex-end;'}
        >
          Header text appears at the top of the registration form.
        </Text>
      </div>
    </div>
  )
}

export default Customization
